import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '@/components/button';
import { Form } from '@/components/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput } from '@/components/inputs/text-input';
import styles from './styles.module.css';
import { requiredString } from '@/util/validators';
import { Expander } from '@/components/expander';
import { SurgeryIcon } from '../icons/surgery-icon';
import { MedicalHistoryItemList } from '../medical-history-item-list';
import { InputLabel } from '@/components/inputs/input-label';
import { Surgery } from '@/hooks/use-medical-history-state';

const surgerySchema = z.object({
  surgery: requiredString(),
});

type FormValues = z.infer<typeof surgerySchema>;

interface SurgeriesFormProps {
  onRemove: (id: string) => void;
  onAdd: (surgery: string) => void;
  surgeries: Surgery[];
  isOpen?: boolean;
  onExpanderClick?: () => void;
}

const translationBase = 'visit.medicalHistory.surgeriesForm';

export function SurgeriesForm({
  onRemove,
  onAdd,
  surgeries,
  isOpen,
  onExpanderClick,
}: SurgeriesFormProps) {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(surgerySchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      surgery: '',
    },
  });

  const onSubmit = (data: FormValues) => {
    onAdd(data.surgery);
    form.reset();
  };

  return (
    <Expander
      icon={<SurgeryIcon className={styles.icon} />}
      title={t(`${translationBase}.title`)}
      indicatorText={t(`${translationBase}.indicatorText`)}
      collapsedText={surgeries.map((surgery) => surgery.text).join(', ')}
      isOpen={isOpen}
      onClick={onExpanderClick}
    >
      <Form onSubmit={onSubmit} form={form} className={styles.form}>
        <div className={styles.formContainer}>
          <InputLabel label={t(`${translationBase}.surgeryLabel`)} htmlFor="surgery-input" />
          <TextInput
            id="surgery-input"
            data-testid="medical-history-surgery-input"
            className={styles.input}
            name="surgery"
            placeholder={t(`${translationBase}.surgeryPlaceholder`)}
            withIconsIndicators={false}
            autoComplete="off"
          />
          <Button
            type="submit"
            className={styles.addButton}
            data-testid="medical-history-surgeries-add"
          >
            {t(`${translationBase}.addButton`)}
          </Button>
        </div>
        <MedicalHistoryItemList items={surgeries} onRemove={onRemove} />
      </Form>
    </Expander>
  );
}
